// EventEditorSheet — shared event add/edit/delete sheet (events unification).
//
// Used from two places: ChurchInfoSettingsSheet (BibleApp.jsx, series-level
// — no `occurrenceDate`) and This Week's "Coming up" (occurrence-level —
// `occurrenceDate` is passed, which is what triggers the "Just this one" /
// "This and all future" choice on save/delete).
//
// Props:
//   event          — the church_events row being edited, or null for "add new"
//   occurrenceDate — 'YYYY-MM-DD' of the tapped occurrence, or null/undefined
//                    when opened series-level (church settings, or a brand
//                    new event with no occurrence context yet)
//   churchId       — required for a new event's insert
//   churchName     — optional; when given, shown in the title ("New event ·
//                    St John's Church") so a super admin always knows which
//                    church they're editing (docs/FIX-PLANS.md §1e). Omitted
//                    for everyone else, so their title is unchanged.
//   onClose()
//   onSaved()      — called after a successful save/delete, before onClose
//   initialAction  — "delete" jumps straight to the delete confirm/scope-choice
//                    flow on mount (a direct "delete" tap on an event row,
//                    rather than "Edit"); omitted/undefined for the normal
//                    add/edit form

import { useEffect, useState } from "react";
import { supabase } from "../supabase";

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const GOLD = "var(--gold)";

// Without this, a hung request (e.g. a stuck auth-token refresh — known to
// happen in the Median WebView wrapper after the app's been backgrounded)
// left "Saving…" spinning forever with no way out. Races the real request
// against a timeout so the button always comes back and the user gets an
// actionable error instead of a dead sheet.
function withTimeout(promise, ms = 15000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("Taking longer than expected — check your connection and try again.")), ms)),
  ]);
}

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
function ordinal(n) {
  return n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : n === 4 ? "4th" : "5th";
}
export function nthWeekdayLabel(dateStr) {
  if (!dateStr) return "this weekday";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "this weekday";
  return `the ${ordinal(Math.ceil(d.getDate() / 7))} ${WEEKDAY_NAMES[d.getDay()]}`;
}
export function dayOfMonthLabel(dateStr) {
  if (!dateStr) return "this day";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "this day";
  return `day ${d.getDate()}`;
}

export default function EventEditorSheet({ event, occurrenceDate, churchId, churchName, onClose, onSaved, initialAction }) {
  const isRecurringSeries = (event?.recurrence || "none") !== "none";
  const [name, setName] = useState(event?.name || "");
  const [eventDate, setEventDate] = useState(event?.event_date || "");
  const [eventTime, setEventTime] = useState((event?.event_time || "").slice(0, 5));
  const [location, setLocation] = useState(event?.location || "");
  const [description, setDescription] = useState(event?.description || "");
  const [recurrence, setRecurrence] = useState(event?.recurrence || "none");
  const [endsOn, setEndsOn] = useState(event?.repeat_until || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [scopeChoice, setScopeChoice] = useState(null); // null | 'save' | 'delete'

  const repeatOptions = [
    ["none", "Never"],
    ["weekly", "Weekly"],
    ["fortnightly", "Every 2 weeks"],
    ["monthly_date", `Monthly on ${dayOfMonthLabel(eventDate)}`],
    ["monthly_weekday", `Monthly on ${nthWeekdayLabel(eventDate)}`],
  ];

  async function doSave(scope) {
    if (!name.trim() || !eventDate) { setError("Name and date are required."); return; }
    setSaving(true); setError("");
    try {
      if (occurrenceDate && scope === "one") {
        const { error: e } = await withTimeout(supabase.from("event_exceptions").upsert({
          event_id: event.id, occurrence_date: occurrenceDate,
          override_title: name.trim(), override_time: eventTime || null, override_location: location.trim() || null,
        }, { onConflict: "event_id,occurrence_date" }));
        if (e) throw e;
      } else {
        // churchId sometimes starts as "" (BibleApp's activeChurchId state
        // defaults to "" before the profile loads) — sending that straight
        // to a uuid column fails with the cryptic "invalid syntax for type
        // uuid: ''" instead of a useful message. Catch it here instead.
        if (!churchId) { setSaving(false); setError("Could not tell which church this is for — close and try again."); return; }
        const payload = {
          church_id: churchId,
          name: name.trim(),
          event_date: eventDate,
          event_time: eventTime || null,
          location: location.trim() || null,
          description: description.trim() || null,
          recurrence,
          repeat_until: endsOn || null,
        };
        if (event?.id) {
          const { error: e } = await withTimeout(supabase.from("church_events").update(payload).eq("id", event.id));
          if (e) throw e;
        } else {
          const { error: e } = await withTimeout(supabase.from("church_events").insert(payload));
          if (e) throw e;
        }
      }
      setSaving(false);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setSaving(false);
      setError(e.message || String(e));
    }
  }

  async function doDelete(scope) {
    setSaving(true); setError("");
    try {
      if (occurrenceDate && scope === "one") {
        const { error: e } = await withTimeout(supabase.from("event_exceptions").upsert({
          event_id: event.id, occurrence_date: occurrenceDate, cancelled: true,
        }, { onConflict: "event_id,occurrence_date" }));
        if (e) throw e;
      } else if (occurrenceDate && scope === "future") {
        if (!isRecurringSeries) {
          const { error: e } = await withTimeout(supabase.from("church_events").delete().eq("id", event.id));
          if (e) throw e;
        } else {
          const dayBefore = new Date(`${occurrenceDate}T00:00:00`);
          dayBefore.setDate(dayBefore.getDate() - 1);
          const until = dayBefore.toISOString().slice(0, 10);
          const { error: e } = await withTimeout(supabase.from("church_events").update({ repeat_until: until }).eq("id", event.id));
          if (e) throw e;
        }
      } else {
        const { error: e } = await withTimeout(supabase.from("church_events").delete().eq("id", event.id));
        if (e) throw e;
      }
      setSaving(false);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setSaving(false);
      setError(e.message || String(e));
    }
  }

  function handleSaveClick() {
    if (occurrenceDate && isRecurringSeries) { setScopeChoice("save"); return; }
    doSave(null);
  }
  function handleDeleteClick() {
    if (!window.confirm(occurrenceDate ? "Delete this event?" : "Delete this event series? This removes every occurrence.")) return;
    if (occurrenceDate && isRecurringSeries) { setScopeChoice("delete"); return; }
    doDelete(occurrenceDate ? "future" : null);
  }

  // A direct "delete" tap from This Week's event row (rather than "Edit")
  // jumps straight to the same confirm/scope-choice flow as the Delete
  // button below, instead of duplicating that logic at the call site.
  useEffect(() => {
    if (initialAction === "delete" && event?.id) handleDeleteClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Tapping outside no longer discards the form — only Cancel does. */}
      <div style={S.backdrop}/>
      <div style={S.sheet}>
        <div style={S.grab}/>
        {scopeChoice ? (
          <div style={{ padding: "4px 20px 20px" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 14 }}>
              {scopeChoice === "save" ? "Apply this change to…" : "Delete…"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button type="button" disabled={saving} onClick={() => (scopeChoice === "save" ? doSave("one") : doDelete("one"))} style={S.scopeBtn}>
                Just this one
              </button>
              <button type="button" disabled={saving} onClick={() => (scopeChoice === "save" ? doSave("future") : doDelete("future"))} style={S.scopeBtn}>
                This and all future
              </button>
              <button type="button" disabled={saving} onClick={() => setScopeChoice(null)} style={S.cancelBtn}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: "4px 20px 20px" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 10 }}>
              {event?.id ? "Edit event" : "New event"}{churchName ? ` · ${churchName}` : ""}
            </div>
            <input style={S.input} placeholder="Event name *" value={name} onChange={e => setName(e.target.value)}/>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {/* Native date/time inputs don't reliably show a placeholder
                  when empty across browsers/WebViews, so label them like
                  every other field instead of relying on that. */}
              <div style={{ flex: 1 }}>
                <label style={S.label}>Date *</label>
                <input style={S.input} type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}/>
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Time</label>
                <input style={S.input} type="time" value={eventTime} onChange={e => setEventTime(e.target.value)}/>
              </div>
            </div>
            <input style={{ ...S.input, marginTop: 8 }} placeholder="Location" value={location} onChange={e => setLocation(e.target.value)}/>
            <textarea style={{ ...S.input, marginTop: 8 }} rows={3} placeholder="Description" value={description} onChange={e => setDescription(e.target.value)}/>

            <label style={S.label}>Repeats</label>
            <select style={S.input} value={recurrence} onChange={e => setRecurrence(e.target.value)}>
              {repeatOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>

            {recurrence !== "none" && (
              <>
                <label style={S.label}>Ends</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button type="button" onClick={() => setEndsOn("")}
                    style={{ ...S.scopeBtnSmall, ...(endsOn ? {} : S.scopeBtnSmallActive) }}>Never</button>
                  <input style={{ ...S.input, flex: 1 }} type="date" value={endsOn} onChange={e => setEndsOn(e.target.value)}/>
                </div>
              </>
            )}

            {error && <div style={S.error}>{error}</div>}

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button type="button" disabled={saving} onClick={handleSaveClick} style={S.saveBtn}>
                {saving ? "Saving…" : (event?.id ? "Update event" : "Add event")}
              </button>
              <button type="button" disabled={saving} onClick={onClose} style={S.cancelBtn}>Cancel</button>
            </div>
            {event?.id && (
              <button type="button" disabled={saving} onClick={handleDeleteClick} style={S.deleteBtn}>Delete</button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

const S = {
  backdrop: { position: "fixed", inset: 0, zIndex: 620, background: "rgba(0,0,0,0.5)" },
  sheet: { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 621, background: "var(--cream)", borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: "85vh", overflowY: "auto", fontFamily: SANS },
  grab: { width: 38, height: 4, borderRadius: 2, background: "rgba(31,27,22,0.18)", margin: "10px auto" },
  label: { fontSize: 11, color: "var(--stone-dim)", display: "block", marginTop: 10, marginBottom: 4, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" },
  input: { width: "100%", background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 10, padding: "11px 14px", color: "var(--ink)", fontSize: 14, fontFamily: SANS, boxSizing: "border-box", outline: "none" },
  error: { marginTop: 8, padding: "8px 12px", background: "rgba(249,112,102,0.10)", border: "1px solid rgba(249,112,102,0.30)", borderRadius: 8, fontSize: 13, color: "#C8372A" },
  saveBtn: { flex: 1, background: GOLD, color: "var(--ink)", border: "none", borderRadius: 8, padding: 11, fontSize: 13.5, fontWeight: 800, cursor: "pointer", fontFamily: SANS },
  cancelBtn: { background: "transparent", color: "var(--stone)", border: "1px solid var(--border)", borderRadius: 8, padding: "11px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS },
  deleteBtn: { marginTop: 10, width: "100%", background: "none", border: "1px solid rgba(249,112,102,0.40)", color: "#C8372A", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: SANS },
  scopeBtn: { textAlign: "left", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px", fontSize: 14, fontWeight: 600, color: "var(--ink)", cursor: "pointer", fontFamily: SANS },
  scopeBtnSmall: { background: "var(--white)", border: "1px solid var(--border)", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, color: "var(--stone)", cursor: "pointer", fontFamily: SANS },
  scopeBtnSmallActive: { background: GOLD, color: "var(--ink)", border: "none" },
};
